import { FormEvent, useEffect, useState } from 'react';
import { CalendarWorkDay, EnterpriseCalendar } from '../../services/enterpriseCalendarsApi';
import { ModalPortalShell } from '../ui/ModalPortalShell';
import { DAY_LABELS, defaultWorkDays } from './calendarUtils';

type CalendarWorkScheduleModalProps = {
  open: boolean;
  calendar: EnterpriseCalendar | null;
  onClose: () => void;
  onSubmit: (workDays: CalendarWorkDay[]) => Promise<void>;
};

export function CalendarWorkScheduleModal({
  open,
  calendar,
  onClose,
  onSubmit,
}: CalendarWorkScheduleModalProps) {
  const [workDays, setWorkDays] = useState<CalendarWorkDay[]>(defaultWorkDays());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !calendar) return;
    setWorkDays(
      calendar.workDaySchedules.length > 0
        ? calendar.workDaySchedules.map((day) => ({ ...day }))
        : defaultWorkDays(),
    );
  }, [open, calendar]);

  if (!open || !calendar) return null;

  function updateWorkDay(index: number, patch: Partial<CalendarWorkDay>): void {
    setWorkDays((current) =>
      current.map((day, dayIndex) => (dayIndex === index ? { ...day, ...patch } : day)),
    );
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    try {
      await onSubmit(workDays);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortalShell
      open={open}
      panelClassName="max-w-2xl rounded-2xl bg-white shadow-xl"
      overlayClassName="z-50"
    >
      <form onSubmit={(event) => void handleSubmit(event)} className="flex min-h-0 flex-1 flex-col">
        <div className="shrink-0 border-b border-slate-100 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Horários da semana</h2>
          <p className="mt-1 text-sm text-slate-500">{calendar.name}</p>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-4">
          {workDays.map((day, index) => (
            <div
              key={day.dayOfWeek}
              className="grid grid-cols-[1.5fr_auto_1fr_1fr] items-center gap-3 rounded-xl border border-slate-200 px-3 py-2"
            >
              <span className="text-sm font-medium text-slate-800">{DAY_LABELS[day.dayOfWeek]}</span>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={day.isWorkDay}
                  onChange={(e) => updateWorkDay(index, { isWorkDay: e.target.checked })}
                />
                Útil
              </label>
              <label className="block text-xs text-slate-500">
                Início
                <input
                  type="time"
                  className="w-full rounded-lg border border-slate-300 px-2 py-1"
                  value={(day.workStartTime ?? '').slice(0, 5)}
                  onChange={(e) => updateWorkDay(index, { workStartTime: `${e.target.value}:00` })}
                  disabled={!day.isWorkDay}
                />
              </label>
              <label className="block text-xs text-slate-500">
                Fim
                <input
                  type="time"
                  className="w-full rounded-lg border border-slate-300 px-2 py-1"
                  value={(day.workEndTime ?? '').slice(0, 5)}
                  onChange={(e) => updateWorkDay(index, { workEndTime: `${e.target.value}:00` })}
                  disabled={!day.isWorkDay}
                />
              </label>
            </div>
          ))}
        </div>

        <div className="erp-btn-actions shrink-0 border-t border-slate-100 px-6 py-4">
          <button type="button" className="erp-btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="erp-btn-primary">
            {saving ? 'Salvando...' : 'Salvar horários'}
          </button>
        </div>
      </form>
    </ModalPortalShell>
  );
}
